import { SecondaryTitle } from "@/components/main-title";
import { MinimalDataTable } from "@/components/product/minimal-table";
import { InventoryEventsTable } from "@/components/product/inventory-events-table";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { productQueryOpts, useDeleteProducts } from "@/hooks/product";
import { currentUserQueryOpts } from "@/hooks/user";
import { useAuth } from "@/providers/auth";
import { useQuery } from "@tanstack/react-query";
import {
  createFileRoute,
  Link,
  useCanGoBack,
  useNavigate,
  useRouter,
} from "@tanstack/react-router";

import { isGated } from "@/hooks/gated";
import GatedOverlay from "@/components/ui/gated-overlay";
import { Separator } from "@/components/ui/separator";
import { SidebarTrigger, useSidebar } from "@/components/ui/sidebar";
import { inventoryPath } from "@/constants";
import { Button, buttonVariants } from "@/components/ui/button";
import { ChevronRight, PackageIcon } from "lucide-react";
import { cn, getOrgIdWithFallback } from "@/lib/utils";
import { useOrg } from "@/providers/organization";
import { PageLoader } from "@/components/ui/page-loader";
import { DeleteConfirmation } from "@/components/inventory/delete-confirmation";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { createColumns } from "@/components/product/table-helper";

export const Route = createFileRoute("/$org/products/$id")({
  component: RouteComponent,
  loader: async ({ context, params }) => {
    const { getToken, setToken, handleTokenInvalid } = context.auth;
    const orgId = getOrgIdWithFallback(context.org.orgId);

    const gated = await isGated(
      {
        token: getToken() as string,
        setToken,
        orgId: orgId,
      },
      handleTokenInvalid,
    );

    context.queryClient.ensureQueryData(
      productQueryOpts(params.id, {
        token: getToken() ?? "",
        setToken,
        orgId: orgId,
      }),
    );

    return { gated: gated?.is_gated };
  },
});

function RouteComponent() {
  const { t } = useTranslation();
  const { gated } = Route.useLoaderData();
  const { isMobile } = useSidebar();
  const { org, id } = Route.useParams();
  const { orgId } = useOrg();
  const { getToken, setToken } = useAuth();
  const navigate = useNavigate();
  const deleteMutation = useDeleteProducts();
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const { history } = useRouter();
  const canGoBack = useCanGoBack();

  const inventoryPage = `/${org}${inventoryPath}`;

  const { data: product } = useQuery(
    productQueryOpts(id, {
      orgId: orgId,
      token: getToken() ?? "",
      setToken,
    }),
  );

  const { data: user } = useQuery(
    currentUserQueryOpts({
      orgId: orgId,
      token: getToken() ?? "",
      setToken,
    }),
  );

  const columns = createColumns(user?.currency || "USD");

  const handleDelete = () => {
    deleteMutation.mutate(
      { ids: [id], all: false },
      {
        onSuccess: () => {
          toast.info(t("product.deleting"));
          navigate({ to: inventoryPage });
        },
      },
    );
  };

  if (gated) {
    return <GatedOverlay org={org} />;
  }

  if (!product || !user) {
    return <PageLoader />;
  }

  return (
    <div className="flex flex-col max-w-5xl mx-auto space-y-4">
      <div className="pb-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-1">
            {isMobile && (
              <>
                <SidebarTrigger className="-ml-1" />
                <Separator orientation="vertical" className="mr-2 h-4" />
              </>
            )}
            {canGoBack ? (
              <Button onClick={() => history.back()} variant={"ghost"}>
                <PackageIcon className="h-5 w-5" />
              </Button>
            ) : (
              <Link
                to={inventoryPage}
                className={cn(buttonVariants({ variant: "ghost" }), "")}
              >
                <PackageIcon className="h-5 w-5" />
              </Link>
            )}
            <ChevronRight className="h-3 w-3" />
            <h1 className="text-2xl font-medium">{product?.title}</h1>
          </div>
        </div>

        <Separator className="my-2" />
      </div>

      <div className="rounded flex-1 space-y-8">
        <div className="space-y-8">
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="title" className="font-normal">
              {t("product.title")}
            </Label>
            <Input
              id="title"
              type="text"
              value={product?.title}
              name="title"
              disabled
            />
          </div>
          <div className="flex flex-col space-y-1.5">
            <Label htmlFor="sku" className="font-normal">
              {t("product.sku")}
            </Label>
            <Input
              id="sku"
              type="text"
              value={product?.sku ?? ""}
              name="sku"
              disabled
              className="w-96"
            />
          </div>
        </div>
        <div>
          <SecondaryTitle
            title={t("product.variantsTitle")}
            description={t("product.variantsDescription")}
          />
          <MinimalDataTable columns={columns} data={product?.variants ?? []} />
        </div>
        <div>
          <InventoryEventsTable productID={id} />
        </div>
        <div className="flex justify-end">
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setShowDeleteConfirmation(true)}
          >
            {t("common.delete")}
          </Button>
        </div>
      </div>

      <DeleteConfirmation
        isOpen={showDeleteConfirmation}
        onOpenChange={setShowDeleteConfirmation}
        onConfirm={handleDelete}
        selectedCount={1}
        isLoading={deleteMutation.isPending}
      />
    </div>
  );
}
